import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { agreementService, SignedAgreement } from '../services/agreementService';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';

const LegalSection: React.FC = () => {
  const { user } = useAuth();
  const [agreements, setAgreements] = useState<SignedAgreement[]>([]);
  const [filteredAgreements, setFilteredAgreements] = useState<SignedAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAgreements();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAgreements(agreements);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = agreements.filter(
        (agreement) =>
          agreement.user_name?.toLowerCase().includes(term) ||
          agreement.user_email?.toLowerCase().includes(term) ||
          agreement.agreement_name?.toLowerCase().includes(term) ||
          agreement.username?.toLowerCase().includes(term)
      );
      setFilteredAgreements(filtered);
    }
  }, [searchTerm, agreements]);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await agreementService.getAllSignedAgreements();
      setAgreements(data);
      setFilteredAgreements(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch signed agreements');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (userAgreementId: number) => {
    try {
      await agreementService.downloadAgreementPdf(userAgreementId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to download PDF');
    }
  };

  const handleView = async (userAgreementId: number) => {
    try {
      // Open in new tab
      const response = await fetch(
        `${config.apiBaseUrl}/agreements/download/${userAgreementId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err: any) {
      alert('Failed to view PDF');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => role.slug === 'pmp_admin');
  };

  if (!isPmpAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. This section is only available to PMP Admins.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Legal Section - Signed Agreements
        </Typography>
        <Chip 
          label={`${filteredAgreements.length} Agreement${filteredAgreements.length !== 1 ? 's' : ''}`} 
          color="primary" 
          variant="outlined"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by user name, email, or agreement name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>User Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Agreement Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Version</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Signed Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgreements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    {searchTerm ? 'No agreements found matching your search.' : 'No signed agreements found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAgreements.map((agreement) => (
                <TableRow key={agreement.id} hover>
                  <TableCell>{agreement.user_name || 'N/A'}</TableCell>
                  <TableCell>{agreement.user_email || 'N/A'}</TableCell>
                  <TableCell>{agreement.agreement_name || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`v${agreement.agreement_version}`} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(agreement.signed_at)}</TableCell>
                  <TableCell>
                    {agreement.has_pdf ? (
                      <Chip label="Signed" color="success" size="small" />
                    ) : (
                      <Chip label="No PDF" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      {agreement.has_pdf && (
                        <>
                          <Tooltip title="View PDF">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleView(agreement.id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => handleDownload(agreement.id)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {!agreement.has_pdf && (
                        <Typography variant="caption" color="textSecondary">
                          No PDF
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">
          This section displays all user agreements signed across the platform. 
          You can view and download signed agreement PDFs.
        </Typography>
      </Box>
    </Box>
  );
};

export default LegalSection;

